import { useEffect, useCallback, useRef } from "react";
import { useDispatch } from "react-redux";
import { closeFullscreen } from "../State/Slices/settingsSlice";

// Custom hook to manage fullscreen controls using keyboard inputs
const useFullscreenControls = () => {
  // Get the dispatch function from Redux to dispatch actions
  const dispatch = useDispatch();
  // Create a ref to track if the space key is currently pressed
  const isSpacePressed = useRef(false);

  // Memoized function to handle keydown events
  const handleKeyDown = useCallback(
    (e) => {
      // If the Escape key is pressed, dispatch the action to close fullscreen
      if (e.key === "Escape") {
        dispatch(closeFullscreen());
      }
      // If the space key is pressed and wasn't already pressed, set the ref to true
      else if (e.key === " " && !isSpacePressed.current) {
        isSpacePressed.current = true;
        // Handle space key press logic here (currently no additional logic)
      }
    },
    [dispatch] // Dependencies array ensures the function is recreated if dispatch changes
  );

  // Memoized function to handle keyup events
  const handleKeyUp = useCallback(
    (e) => {
      // If the space key is released, set the ref to false
      if (e.key === " ") {
        isSpacePressed.current = false;
      }
    },
    [] // No dependencies, so this function is stable across renders
  );

  // Memoized function to handle changes in fullscreen state
  const handleFullscreenChange = useCallback(() => {
    // If the document is not in fullscreen mode, dispatch the action to close fullscreen
    if (!document.fullscreenElement) {
      dispatch(closeFullscreen());
    }
  }, [dispatch]); // Dependencies array ensures the function is recreated if dispatch changes

  // useEffect to add and clean up event listeners
  useEffect(() => {
    // Add event listeners for keydown, keyup, and fullscreen changes
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    document.addEventListener("fullscreenchange", handleFullscreenChange);

    // Cleanup function to remove event listeners when the component unmounts
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, [handleKeyDown, handleKeyUp, handleFullscreenChange]); // Dependencies array ensures effect runs when these functions change

  // This hook does not return any value or component
  return null;
};

// Export the custom hook as the default export
export default useFullscreenControls;
